import type { Message, ToolPart } from "./opencodeTypes"

// What is actually taking up the context window, by role.
//
// Ported from agent/packages/app/src/components/session/session-context-breakdown.ts
// and adapted: parts live on the message here rather than in a separate store,
// and a tool's output arrives as `content` blocks rather than as the single
// `output` / `raw` strings the upstream version reads (see the note on
// ToolContentBlock in opencodeTypes.ts).
//
// Everything below is an *estimate*, and deliberately so. The provider reports
// one input-token figure for the whole prompt and never says which part of the
// conversation it came from, so the only way to answer "what is filling this
// up" is to measure the text and apportion. Four characters per token is the
// usual rough ratio; the segments are then scaled so they sum to the input
// count the provider did report, which keeps the bar honest at the total even
// where it is wrong in the middle.

export type BreakdownKey = "system" | "user" | "assistant" | "tool" | "other"

export interface BreakdownSegment {
  key: BreakdownKey
  tokens: number
  /** Share of the input total, 0–100, unrounded — for the bar's width. */
  width: number
  /** The same share to one decimal place — for the label. */
  percent: number
}

const estimateTokens = (chars: number) => Math.ceil(chars / 4)

function toolChars(part: ToolPart): number {
  // The input is JSON we never see rendered, so its keys are counted at a flat
  // rate rather than stringified — the upstream version does the same.
  const state = part.state
  const input = state.input && typeof state.input === "object" ? Object.keys(state.input).length * 16 : 0
  const content = "content" in state ? (state.content ?? []) : []
  const output = content.reduce((sum, block) => sum + (block.text?.length ?? 0), 0)
  const error = state.status === "error" ? String(state.error ?? "").length : 0
  return input + output + error
}

function messageChars(message: Message): Partial<Record<BreakdownKey, number>> {
  switch (message.type) {
    case "user":
      return { user: message.text.length }

    // Everything the platform injects rather than the person: the environment
    // header, compaction summaries, and anything a tool wrote back as a message.
    case "system":
    case "synthetic":
      return { system: message.text.length }
    case "compaction":
      return { system: message.summary.length }
    case "shell":
      return { tool: message.command.length + message.output.length }

    case "assistant": {
      let assistant = 0
      let tool = 0
      for (const part of message.content ?? []) {
        if (part.type === "text" || part.type === "reasoning") assistant += part.text.length
        else if (part.type === "tool") tool += toolChars(part)
      }
      return { assistant, tool }
    }

    default:
      return {}
  }
}

/**
 * The segments of the context bar.
 *
 * `input` is the provider's own prompt-token count for the most recent turn —
 * the figure being explained. Without it there is nothing to scale to and the
 * answer is an empty list rather than a bar of raw guesses.
 */
export function contextBreakdown(messages: Message[], input: number): BreakdownSegment[] {
  if (!input) return []

  const chars: Record<BreakdownKey, number> = { system: 0, user: 0, assistant: 0, tool: 0, other: 0 }
  for (const message of messages) {
    for (const [key, value] of Object.entries(messageChars(message))) {
      chars[key as BreakdownKey] += value ?? 0
    }
  }

  const tokens = {
    system: estimateTokens(chars.system),
    user: estimateTokens(chars.user),
    assistant: estimateTokens(chars.assistant),
    tool: estimateTokens(chars.tool),
  }
  const estimated = tokens.system + tokens.user + tokens.assistant + tokens.tool

  // Under the real count, the difference is everything we cannot see — the
  // system prompt, tool schemas, the agent's own instructions — and it is shown
  // as "other" rather than quietly dropped. Over it, the estimate was too
  // generous and every segment is scaled down to fit.
  const scaled =
    estimated <= input
      ? { ...tokens, other: input - estimated }
      : (() => {
          const factor = input / estimated
          const down = {
            system: Math.floor(tokens.system * factor),
            user: Math.floor(tokens.user * factor),
            assistant: Math.floor(tokens.assistant * factor),
            tool: Math.floor(tokens.tool * factor),
          }
          const sum = down.system + down.user + down.assistant + down.tool
          return { ...down, other: Math.max(0, input - sum) }
        })()

  return (Object.keys(scaled) as BreakdownKey[])
    .filter((key) => scaled[key] > 0)
    .map((key) => ({
      key,
      tokens: scaled[key],
      width: (scaled[key] / input) * 100,
      percent: Math.round((scaled[key] / input) * 1000) / 10,
    }))
}
